import { orgBadgeText } from "@/components/format";
import { ORG_LOGO_EXT } from "@/lib/org-logos";

/**
 * Organization mark: the real logo when we have one harvested into
 * public/logos/<key>.<ext> (see scripts/fetch-logos.ts) — mostly official SVGs
 * that stay crisp at any DPI, else a high-res PNG/JPG — otherwise the acronym
 * badge. Plain <img> on purpose — 90+ small marks would burn next/image
 * optimizer quota for no visible gain at these sizes, and next/image can't
 * optimize SVG anyway.
 *
 * Decorative by contract: the org name is always adjacent text, so the mark
 * is aria-hidden with an empty alt.
 */
export function OrgLogo({
  orgKey,
  acronym,
  name = null,
  className = "h-10 w-10",
}: {
  orgKey: string;
  acronym: string | null;
  /** Org name — used to derive a compact badge when acronym is absent/long. */
  name?: string | null;
  /** Size + layout classes from the call site (e.g. "mt-0.5 h-10 w-10"). */
  className?: string;
}) {
  const ext = ORG_LOGO_EXT[orgKey];
  if (ext) {
    return (
      <span
        aria-hidden
        className={`flex shrink-0 items-center justify-center overflow-hidden rounded-md border border-neutral-200 bg-white p-1 ${className}`}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={`/logos/${orgKey}.${ext}`}
          alt=""
          loading="lazy"
          className="h-full w-full object-contain"
        />
      </span>
    );
  }
  return (
    <span
      aria-hidden
      className={`flex shrink-0 items-center justify-center overflow-hidden rounded-md bg-brand-50 p-0.5 text-center text-[10px] leading-tight font-bold tracking-tight text-brand-700 ${className}`}
    >
      {orgBadgeText(acronym, name, orgKey)}
    </span>
  );
}
